import { GuestAppSubStore, GuestAppSubStoreArgs } from "@/store/types";
import { computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { Maybe } from "@/domains/common/types";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { createProxyWithIsDeleted } from "@/domains/react/useObjectAfterDeletion";
import { useEffect } from "react";
import { useMatch } from "react-router";
import { appRoutes } from "@/app/router";
import { INoteObservable } from "@/store/note";
import { windowModule } from "@/modules/window";

export class AppStoreGuestNotesViewPageStore
  extends GuestAppSubStore
  implements INotesViewPageStore<INoteObservable>
{
  noteId: string | null = null;
  /** This is a proxy with isDeleted=true after the note is gone. */
  itemObservable: Maybe<INoteObservable>;

  constructor(injectedDeps: GuestAppSubStoreArgs) {
    super(injectedDeps);
    makeObservable(this, {
      openFileUploadRejectedModal: true,
      openImageUploadRejectedModal: true,
      goToMention: false,
      onMentionMouseOver: false,
      onMentionMouseOut: false,
      myAccount: computed,
      showNotFound: computed,
      isLoading: computed,
      useNoteIdFromRoutePath: false,
      noteId: observable,
      itemObservable: observable,
      isUpToDate: computed,
      note: computed,
    });

    reaction(
      () => this.note,
      (maybeNote, previousNote) => {
        const note = (() => {
          if (maybeNote) return maybeNote;
          if (!previousNote || !this.noteId) return;

          return createProxyWithIsDeleted(previousNote);
        })();
        runInAction(() => {
          this.itemObservable = note;
        });
      }
    );
  }

  get isUpToDate() {
    return this.store.sync.isUpToDate;
  }

  get myAccount() {
    return undefined;
  }

  get note(): Maybe<INoteObservable> {
    const noteId = this.noteId;
    if (!noteId) return;
    return this.store.note.getNoteObservableById({ noteId });
  }

  get showNotFound() {
    return false;
  }

  get isLoading() {
    return false; // Guest mode doesn't support hydration
  }

  goToMention: INotesViewPageStore["goToMention"] = ({ url }) => {
    windowModule.openInNewTab({ url: new URL(url) });
  };

  onMentionMouseOver: INotesViewPageStore["onMentionMouseOver"] = () => {
    /** Not currently implemented in guest mode. */
  };

  onMentionMouseOut: INotesViewPageStore["onMentionMouseOut"] = () => {
    /** Not currently implemented in guest mode. */
  };

  openFileUploadRejectedModal: INotesViewPageStore["openFileUploadRejectedModal"] = () => {
    /** Not currently implemented in guest mode. */
  };
  openImageUploadRejectedModal: INotesViewPageStore["openImageUploadRejectedModal"] = () => {
    /** Not currently implemented in guest mode. */
  };

  useNoteIdFromRoutePath() {
    const noteIdMatch = useMatch(
      appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true })
    );
    const maybeNoteIdParam = noteIdMatch?.params.noteId ?? null;
    useEffect(() => {
      runInAction(() => {
        this.noteId = maybeNoteIdParam;
      });
    }, [maybeNoteIdParam]);
  }
}
